import React, { useEffect, useState } from 'react'
import styled from '@emotion/styled'
import { colors } from 'Themes/Colors'
import { Goal } from 'Generated/graphql-types'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import TrendingDownIcon from '@mui/icons-material/TrendingDown'
import { useQuery } from '@apollo/client'
import { CHART } from '../../../../../Apollo/Queries/Chart'
import { Chart, ChartCalculationOptionsEnum } from '../../GoalPage/types'
import { switchDataVisualization } from '../../GoalPage/utils'

type PercentageCardProps = {
  goal: Goal
}

export const PercentageCard = ({ goal }: PercentageCardProps) => {
  const { data: chartDisplayData } = useQuery(CHART, {
    variables: { goalId: goal.id },
  })

  const [chartData, setChartData] = useState<Chart | null>(
    chartDisplayData?.chart ?? null
  )

  useEffect(() => {
    if (chartDisplayData?.chart) {
      const { chart } = chartDisplayData
      const calculationType =
        chart.calculationType as ChartCalculationOptionsEnum
      const isCumulative =
        calculationType.toLowerCase() ===
        ChartCalculationOptionsEnum.cumulative.toLowerCase()
      const seriesData = isCumulative
        ? switchDataVisualization(
            chart.seriesData,
            ChartCalculationOptionsEnum.cumulative
          )
        : chart.seriesData
      setChartData({
        ...chart,
        seriesData,
        calculationType: isCumulative
          ? ChartCalculationOptionsEnum.cumulative
          : ChartCalculationOptionsEnum.incremental,
      })
    }
  }, [chartDisplayData])

  const hasSeriesData =
    chartData && chartData.seriesData && chartData.seriesData.length > 0

  // Initialize with defaults - no progress and no trend
  let pct = 0
  let trend: 'increase' | 'decrease' | 'none' = 'none'
  let hasData = false

  if (hasSeriesData && chartData.target && chartData.target > 0) {
    // Safely get the index of the last data point
    const dataLength = chartData.seriesData[0]?.data?.length || 0
    const lastEntryIndex = dataLength - 1

    // Only proceed if we have at least one data point
    if (lastEntryIndex >= 0 && dataLength > 0) {
      hasData = true
      // Calculate the sum of all categories for the latest data point
      // This represents the current total progress across all series
      const lastSum = chartData.seriesData.reduce(
        (total, item) => total + (item.data[lastEntryIndex]?.y || 0),
        0
      )

      // Calculate percentage toward the final goal (target)
      // Capped at 100% to prevent showing over 100% completion
      pct = Math.min(Math.round((lastSum / chartData.target) * 100), 100)

      // Calculate trend only if we have at least 2 data points
      if (lastEntryIndex > 0) {
        // Calculate the sum of all categories for the previous data point
        const prevSum = chartData.seriesData.reduce(
          (total, item) => total + (item.data[lastEntryIndex - 1]?.y || 0),
          0
        )

        // Determine trend based on comparison of current vs previous sums
        if (lastSum > prevSum) {
          trend = 'increase'
        } else if (lastSum < prevSum) {
          trend = 'decrease'
        }
        // If lastSum === prevSum, trend remains 'none'
      }
      // If we only have 1 data point, trend remains 'none'
    }
  }

  return (
    <StyledCenterer>
      <ProgressTitle>PROGRESS</ProgressTitle>
      <DonutContainer>
        <DonutChart percent={pct}>
          <DonutHole>
            <DonutPercent>{pct}%</DonutPercent>
            {!hasData && <NoDataIndicator>NO DATA</NoDataIndicator>}
            {hasData && trend === 'increase' && (
              <IncreaseIndicator>
                <TrendingUpIcon fontSize='small' />
                INCREASE
              </IncreaseIndicator>
            )}
            {hasData && trend === 'decrease' && (
              <DecreaseIndicator>
                <TrendingDownIcon fontSize='small' />
                DECREASE
              </DecreaseIndicator>
            )}
            {/* No badge shown when hasData && trend === 'none' */}
          </DonutHole>
        </DonutChart>
      </DonutContainer>
    </StyledCenterer>
  )
}

const StyledCenterer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  height: 100%;
  min-height: 190px;
  padding: 24px;
  border-left: 1px solid #ccc;
  font-family: 'Helvetica Neue' !important;
`

const ProgressTitle = styled.h3`
  color: #224462;
  margin: 0;
  text-align: center;
  letter-spacing: 0.5px;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 30px; /* 250% */
  text-transform: uppercase;
`

const DonutContainer = styled.div`
  position: relative;
  width: 150px;
  height: 140px;
  margin-top: 10px;
`

interface DonutChartProps {
  percent: number
}

const DonutChart = styled.div<DonutChartProps>`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: radial-gradient(closest-side, white 79%, transparent 80% 100%),
    conic-gradient(
      ${colors.darkGreen} ${(props) => Math.min(props.percent, 100)}%,
      #f0f0f7 0
    );
  position: relative;

  /* Add purple ring */
  &::before {
    content: '';
    position: absolute;
    top: 4%;
    left: 4%;
    right: 4%;
    bottom: 4%;
    border-radius: 50%;
    z-index: 1;
  }
`

const DonutHole = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 75%;
  height: 75%;
  background: white;
  border-radius: 50%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 2;
`

const DonutPercent = styled.div`
  color: #333;
  margin-bottom: 5px;
  font-size: 21px;
  font-style: normal;
  font-weight: 400;
  line-height: 30px;
`

const IncreaseIndicator = styled.div`
  display: flex;
  align-items: center;
  background-color: #e7f5e9;
  margin-top: 5px;
  padding: 3px;
  border-radius: 2px;
  svg {
    font-size: 14px;
    margin-right: 2px;
  }

  font-family: 'Source Sans Pro';
  font-size: 8.386px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
`

const DecreaseIndicator = styled.div`
  display: flex;
  align-items: center;
  background-color: #ffe7e7; /* Red color for decrease */
  margin-top: 5px;
  padding: 2px;
  border-radius: 3px;
  svg {
    font-size: 14px;
    margin-right: 2px;
  }
  font-family: 'Source Sans Pro';
  font-size: 8.386px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
`

const NoDataIndicator = styled.div`
  display: flex;
  align-items: center;
  background-color: #f5f5f5; /* Light grey background */
  margin-top: 5px;
  padding: 3px 6px;
  border-radius: 2px;
  font-family: 'Source Sans Pro';
  font-size: 8.386px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
  color: #666;
`
